import { isAxiosError } from 'axios';
import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';
import { Pageable, PageResponse } from '@/entities/user/api/user.api';

export interface CommissionSearchRequest {
  referenceYearMonth?: string;
  pharmaCompanyName?: string;
  drugName?: string;
  drugCode?: string;
}

export interface CommissionInfoResponse {
  referenceYearMonth: string;
  drugCode: string;
  drugName: string;
  pharmaCompanyName: string;
  price: number;
  standardCommissionRate: number;
  standardCommissionNote: string;
  incentive: number;
  incentiveNote: string;
  outOfStock: boolean;
  outOfStockFrom: string;
  outOfStockTo: string;
}

export interface CommissionDetailRequest {
  refYm: string;
  drugCode: string;
}

export interface CommissionDetailResponse {
  referenceYearMonth: string;
  drugCode: string;
  drugName: string;
  price: number;
  standardCommissionRate: number;
  standardCommissionNote: string;
  incentive: number;
  incentiveNote: string;
  outOfStock: boolean;
  outOfStockFrom: string;
  outOfStockTo: string;
  pharmaCompanyName: string;
}

export interface CommissionDetailUpdateRequest {
  price: number;
  standardCommissionRate: number;
  standardCommissionNote?: string;
  incentive?: string;
  incentiveNote?: string;
  outOfStock?: boolean;
  outOfStockFrom?: string;
  outOfStockTo?: string;
}

export interface CommissionBatchUpdateItem {
  referenceYearMonth: string;
  pharmaCompanyName?: string;
  drugName?: string;
  drugCode: string;
  price: number;
  standardCommissionRate: number;
  standardCommissionNote?: string;
  incentive?: string;
  incentiveNote?: string;
  outOfStock?: boolean;
  outOfStockFrom?: string;
  outOfStockTo?: string;
}

/** GET /admin/commissions/export 응답 */
export interface CommissionExportResponse {
  fileName: string;
  downloadUrl: string;
  expiresAt: string;
  rowCount: number;
}

export const COMMISSION_EXPORT_EMPTY_CODE = 'COMMISSION_EXPORT_EMPTY';

/** 한 달치가 수만 행이라 기본 10초보다 길게 잡는다 */
export const COMMISSION_EXPORT_TIMEOUT_MS = 900_000;

export const buildCommissionSearchParams = (
  searchRequest: CommissionSearchRequest,
): Record<string, string> => {
  const params: Record<string, string> = {};
  if (searchRequest.referenceYearMonth) {
    params.referenceYearMonth = searchRequest.referenceYearMonth;
  }
  if (searchRequest.pharmaCompanyName) {
    params.pharmaCompanyName = searchRequest.pharmaCompanyName;
  }
  if (searchRequest.drugName) params.drugName = searchRequest.drugName;
  if (searchRequest.drugCode) params.drugCode = searchRequest.drugCode;
  return params;
};

/** 조건에 맞는 수수료가 없으면 404 + COMMISSION_EXPORT_EMPTY */
export const isCommissionExportEmptyError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  const data = error.response?.data as { code?: unknown } | undefined;
  return data?.code === COMMISSION_EXPORT_EMPTY_CODE;
};

export const commissionService = {
  getCommissions: async (
    searchRequest: CommissionSearchRequest,
    pageable: Pageable,
  ): Promise<PageResponse<CommissionInfoResponse>> => {
    const params: Record<string, string | number> = {
      page: pageable.page,
      size: pageable.size,
      ...buildCommissionSearchParams(searchRequest),
    };

    const response = await apiClient.get(createApiPath.commissions.list(), {
      params,
    });
    return response.data;
  },

  /**
   * 정산 수수료 목록 엑셀.
   * 목록과 같은 필터로 서버가 파일을 만든 뒤 서명 URL만 돌려준다.
   */
  exportCommissions: async (
    searchRequest: CommissionSearchRequest,
  ): Promise<CommissionExportResponse> => {
    const response = await apiClient.get<CommissionExportResponse>(
      createApiPath.commissions.export(),
      {
        params: buildCommissionSearchParams(searchRequest),
        timeout: COMMISSION_EXPORT_TIMEOUT_MS,
      },
    );
    return response.data;
  },

  getCommissionDetail: async (
    request: CommissionDetailRequest,
  ): Promise<CommissionDetailResponse> => {
    const response = await apiClient.get(createApiPath.commissions.detail(), {
      params: request,
    });
    return response.data;
  },

  updateCommissionDetail: async (
    refYm: string,
    drugCode: string,
    payload: CommissionDetailUpdateRequest,
  ): Promise<void> => {
    await apiClient.patch(createApiPath.commissions.detail(), payload, {
      params: { refYm, drugCode },
    });
  },

  updateCommissionBatch: async (
    items: CommissionBatchUpdateItem[],
  ): Promise<void> => {
    await apiClient.patch(createApiPath.commissions.batch(), items);
  },
};
